// src/encounter/intelligence/chip-routes.ts
//
// Which engine route answers each chip and lens of the Intelligence rail. The labels are Antonin's catalog CHIPS
// (scrips-signal-ds feat/signal, catalog/src/components/IntelligenceRail/IntelligenceRail.tsx), quoted, never rephrased.
// Design-neutral: the rail calls `routeFor(screen, label)` from its onPick and runs what it returns.
//
// Route kinds (encounter owner, chip and lens routing table, 1 Nov 2026: deterministic first, O1):
//   code          no model: a record fact or a rule result, shown as "From the record" or "Scrips rule"
//                   rules            the rules lane (patient facts)
//                   retest           the re-test rule and its T5 group (recorded due dates only)
//                   absence          the ruled absence lines (what is not on file)
//                   differences      the Differences-on-file rules (documents that disagree, a result repeated)
//                   since-last-visit the code-built new-since-last-visit list
//                   brief            the code-built pre-visit brief: open criticals pinned first, new since the last
//                                    visit, absence lines, retests on the plan, pending reports ("From the record")
//   explain       POST /api/intelligence/explain (use-explain.ts); `part: 'series'` is its series payload
//   next-step     POST /api/intelligence/cards/next-step (use-next-step.ts); proposals only, the rail creates no order
//   run           POST /api/intelligence/run with { agent, actionKind }: no chip runs a model today (owner, after the
//                 value test, 1 Nov 2026). The AI brief returns only when visit dates are read and a golden proves it
//                 never omits an open critical; the plan drafter only when the plan library has entries.
//   needs-document the intake agent; it runs only on a document the clinician opened (never on the chart alone)
//   not-prepared  nothing answers this yet; the card says so honestly and draws no content
//
// Which route answers which question is clinical behaviour, ruled by the encounter owner. A label that is not in the
// table is never sent to an agent (it falls to not-prepared), so a renamed chip cannot reach a model by accident.
// Cross-patient chips (day, panel) are not prepared: every engine route reads one patient's record. The state
// interpreter answers the free Ask question only, never a chip.

export type Screen = 'pre' | 'visit' | 'wrapup' | 'chart' | 'terrain' | 'timeline' | 'workup' | 'careplan' | 'protocol' | 'dashboard' | 'today' | 'patients';

/** The engine agents a chip may run (scrips-clinician-demo src/engine/agents.ts), key and actionKind as the engine reads them. */
export const AGENTS = {
  previsit: { agent: 'pre-visit-brief', actionKind: 'brief.previsit.restate' },
  plan: { agent: 'plan-drafter', actionKind: 'careplan.draft' },
  intake: { agent: 'intake', actionKind: 'intake.file-document' },
} as const;

export type CodeSource = 'rules' | 'retest' | 'absence' | 'differences' | 'since-last-visit' | 'brief';

export type ChipRoute =
  | { kind: 'code'; source: CodeSource }
  | { kind: 'explain'; part: 'card' | 'series' }
  | { kind: 'next-step' }
  | { kind: 'run'; agent: string; actionKind: string }
  | { kind: 'needs-document'; agent: string; actionKind: string }
  | { kind: 'not-prepared' };

const code = (source: CodeSource): ChipRoute => ({ kind: 'code', source });
const EXPLAIN: ChipRoute = { kind: 'explain', part: 'card' };
const SERIES: ChipRoute = { kind: 'explain', part: 'series' };
const NEXT_STEP: ChipRoute = { kind: 'next-step' };
const DOCUMENT: ChipRoute = { kind: 'needs-document', ...AGENTS.intake };
export const NOT_PREPARED: ChipRoute = { kind: 'not-prepared' };

/** Screen → label → route. Every chip and lens in the catalog's CHIPS has a row (the test reads the catalog's list). */
export const CHIP_ROUTES: Record<Screen, Record<string, ChipRoute>> = {
  pre: {
    'Prepare the pre-visit brief': code('brief'),
    Prepare: code('brief'),
    // No route may name a drug or a dose for reconciliation yet; it needs its own ruled lane.
    'Medicines to reconcile': NOT_PREPARED,
    'Patient facts': code('rules'),
  },
  visit: {
    'Explain a body system': EXPLAIN,
    'Draft the plan': NOT_PREPARED,
    Retests: code('retest'),
    Orders: NEXT_STEP,
    'Patient facts': code('rules'),
    Prepare: code('brief'),
  },
  wrapup: {
    'Retests on the plan': code('retest'),
  },
  chart: {
    'Which documents disagree?': code('differences'),
    'What is not on file': code('absence'),
    Documents: DOCUMENT,
    'Read this': DOCUMENT,
  },
  terrain: {
    'Explain a system': EXPLAIN,
    'On file for this system': EXPLAIN,
    Summary: EXPLAIN,
    'Protocol shape': NOT_PREPARED,
  },
  timeline: {
    'Recorded draws over time': SERIES,
    'Result history': SERIES,
    'Read results': EXPLAIN,
    Summary: EXPLAIN,
  },
  workup: {
    'Retests on the plan': code('retest'),
    'Items repeating a result': code('differences'),
    'What we lack': code('absence'),
    Sequencing: code('retest'),
    'Read results': EXPLAIN,
  },
  careplan: {
    Rationale: NOT_PREPARED,
    'Plan drafter': NOT_PREPARED,
    Justify: NOT_PREPARED,
    // Not prepared until "tier" is defined, and until careplan gaps move to the ruled absence lines.
    'Explain tier': NOT_PREPARED,
    'What is missing': NOT_PREPARED,
  },
  protocol: {
    'Protocol shape': NOT_PREPARED,
    'What is missing': NOT_PREPARED,
  },
  dashboard: {
    'Prepare me for the day': NOT_PREPARED,
    'Which notes are not signed?': NOT_PREPARED,
    "Today's list": NOT_PREPARED,
    'Open loops': NOT_PREPARED,
    'Results arrived': NOT_PREPARED,
    'Brief for the next visit': code('brief'),
    'Changes since last visit': code('since-last-visit'),
    'This visit': code('brief'),
    'Next visit': code('brief'),
    'Not on file': code('absence'),
  },
  today: {
    'Brief for the next visit': code('brief'),
    'Changes since last visit': code('since-last-visit'),
    'This visit': code('brief'),
    'Next visit': code('brief'),
    'Not on file': code('absence'),
  },
  patients: {
    'Who is due a retest?': NOT_PREPARED,
    'Which loops are open?': NOT_PREPARED,
    'Due a retest': NOT_PREPARED,
    'Open loops': NOT_PREPARED,
  },
};

/** The route for a chip or lens. Unknown labels and screens are never sent to an agent. */
export function routeFor(screen: Screen, label: string): ChipRoute {
  return CHIP_ROUTES[screen]?.[label] ?? NOT_PREPARED;
}

/**
 * The /run body for a `run` route: the chip's own words are the question, marked `chip: true` (a curated prompt, not
 * free text), with the screen as the tab. Returns null for every other kind, so a caller cannot post a non-run route.
 */
export function runRequestFor(screen: Screen, label: string, patientId: string) {
  const r = routeFor(screen, label);
  if (r.kind !== 'run' || !patientId) return null;
  return { agent: r.agent, actionKind: r.actionKind, question: label, patientId, origin: { tab: screen, question: label, chip: true } };
}
